import { supabase } from './supabase';
import { PAYMENT_METHOD_LABELS } from './paymentMethods';

// ─── Types ───────────────────────────────────────────────────────────────────

export type PaymentMethodBreakdownItem = {
  method: string;
  label: string;
  totalAmount: number;
  count: number;
};

export type AccountPaymentMethodBreakdownItem = {
  accountId: string;
  accountName: string;
  method: string;
  label: string;
  totalAmount: number;
  count: number;
};

// ─── Utilities ────────────────────────────────────────────────────────────────

/**
 * Returns a breakdown of total amounts paid per payment_method_detail
 * for a given user. Excludes soft-deleted records, transfers, and card payments.
 *
 * @param userId  Supabase user ID
 * @param dateFrom  Optional ISO date string (inclusive)
 * @param dateTo    Optional ISO date string (inclusive)
 */
export async function getPaymentMethodBreakdown(
  userId: string,
  dateFrom?: string,
  dateTo?: string
): Promise<PaymentMethodBreakdownItem[]> {
  let query = supabase
    .from('transactions')
    .select('payment_method_detail, amount')
    .eq('user_id', userId)
    .eq('type', 'despesa')
    .eq('payment_method', 'account')
    .in('status', ['pago'])
    .is('deleted_at', null)
    .not('payment_method_detail', 'is', null);

  if (dateFrom) query = query.gte('date', dateFrom);
  if (dateTo)   query = query.lte('date', dateTo);

  const { data, error } = await query;
  if (error || !data) return [];

  const map: Record<string, { totalAmount: number; count: number }> = {};
  for (const row of data as { payment_method_detail: string; amount: number }[]) {
    const key = row.payment_method_detail;
    if (!map[key]) map[key] = { totalAmount: 0, count: 0 };
    map[key].totalAmount += Number(row.amount);
    map[key].count += 1;
  }

  return Object.entries(map)
    .map(([method, stats]) => ({
      method,
      label: PAYMENT_METHOD_LABELS[method] ?? method,
      totalAmount: stats.totalAmount,
      count: stats.count,
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount);
}

/**
 * Returns a breakdown of totals per (account × payment_method_detail).
 * Useful for future reports like "Pix saindo da conta Itaú".
 *
 * @param userId  Supabase user ID
 * @param dateFrom  Optional ISO date string
 * @param dateTo    Optional ISO date string
 */
export async function getAccountPaymentMethodBreakdown(
  userId: string,
  dateFrom?: string,
  dateTo?: string
): Promise<AccountPaymentMethodBreakdownItem[]> {
  let txQuery = supabase
    .from('transactions')
    .select('account_id, payment_method_detail, amount')
    .eq('user_id', userId)
    .eq('type', 'despesa')
    .eq('payment_method', 'account')
    .in('status', ['pago'])
    .is('deleted_at', null)
    .not('payment_method_detail', 'is', null);

  if (dateFrom) txQuery = txQuery.gte('date', dateFrom);
  if (dateTo)   txQuery = txQuery.lte('date', dateTo);

  const [txResult, accResult] = await Promise.all([
    txQuery,
    supabase
      .from('accounts')
      .select('id, name')
      .eq('user_id', userId)
      .is('deleted_at', null),
  ]);

  if (txResult.error || !txResult.data) return [];

  const accountNames: Record<string, string> = {};
  for (const acc of (accResult.data ?? []) as { id: string; name: string }[]) {
    accountNames[acc.id] = acc.name;
  }

  const map: Record<string, { totalAmount: number; count: number }> = {};
  for (const row of txResult.data as { account_id: string; payment_method_detail: string; amount: number }[]) {
    const key = `${row.account_id}__${row.payment_method_detail}`;
    if (!map[key]) map[key] = { totalAmount: 0, count: 0 };
    map[key].totalAmount += Number(row.amount);
    map[key].count += 1;
  }

  return Object.entries(map)
    .map(([key, stats]) => {
      const [accountId, method] = key.split('__');
      return {
        accountId,
        accountName: accountNames[accountId] ?? 'Conta desconhecida',
        method,
        label: PAYMENT_METHOD_LABELS[method] ?? method,
        totalAmount: stats.totalAmount,
        count: stats.count,
      };
    })
    .sort((a, b) => b.totalAmount - a.totalAmount);
}

/**
 * Returns total amount paid by a specific payment method for a given account.
 * Convenience helper for account detail pages.
 */
export async function getMethodTotalForAccount(
  userId: string,
  accountId: string,
  method: string,
  dateFrom?: string,
  dateTo?: string
): Promise<number> {
  let query = supabase
    .from('transactions')
    .select('amount')
    .eq('user_id', userId)
    .eq('account_id', accountId)
    .eq('type', 'despesa')
    .eq('payment_method', 'account')
    .eq('payment_method_detail', method)
    .in('status', ['pago'])
    .is('deleted_at', null);

  if (dateFrom) query = query.gte('date', dateFrom);
  if (dateTo)   query = query.lte('date', dateTo);

  const { data } = await query;
  if (!data) return 0;
  return (data as { amount: number }[]).reduce((s, r) => s + Number(r.amount), 0);
}
